import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragMoveEvent,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import { projectsApi, type Artifact } from "@/services/projectsApi";
import SectionSourceModal from "@/components/SectionSourceModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SortableRow } from "./SortableRow";
import type { Section, SectionSources } from "./SortableRow";
import { useSectionListState } from "./useSectionListState";
import { buildLabelData } from "./sectionLabels";
import type { GridReviewMap } from "@/lib/extractionReview";
import type { Document } from "@/types/models";

type ProjectTemplateData = Awaited<ReturnType<typeof projectsApi.getProjectTemplate>>;

interface OutlineSectionListProps {
  projectId: string;
  activeTemplate: NonNullable<ProjectTemplateData>;
  templateQueryKey: string[];
  documents: Document[];
  extractionPoints: { id: string; name: string; parent_id?: string | null }[];
  extractionDocuments: { documentId: string; projectTemplateId: string }[];
  gridReview: GridReviewMap;
  isExtractionLoading?: boolean;
  hasExtractionError?: boolean;
  filterSectionId?: string | null;
  artifacts?: Artifact[];
}

export const OutlineSectionList = ({ projectId, activeTemplate, templateQueryKey, documents, extractionPoints, extractionDocuments, gridReview, isExtractionLoading, hasExtractionError, filterSectionId, artifacts }: OutlineSectionListProps) => {
  const queryClient = useQueryClient();
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dragDeltaX, setDragDeltaX] = useState(0);

  const {
    sections,
    setSections,
    selectedSources,
    collapsedSections,
    setCollapsedSections,
    toggleCollapse,
    pendingDeleteId,
    setPendingDeleteId,
    confirmDeleteSection,
    sourceModalSectionId,
    setSourceModalSectionId,
    handleSourceSave,
  } = useSectionListState({ projectId, activeTemplate, templateQueryKey, stage: "Outline Creation" });

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over, delta } = event;
    if (!over || active.id === over.id) return;

    const draggedId = active.id as string;
    const targetId = over.id as string;
    const dragged = sections.find((s) => s.id === draggedId);
    const target = sections.find((s) => s.id === targetId);
    if (!dragged || !target) return;

    // Reparent if dragged far enough horizontally
    const INDENT_PX = 50;
    let newParentId = dragged.parent_id;
    if (delta.x > INDENT_PX && !target.parent_id && target.id !== draggedId) {
      newParentId = target.id;
    } else if (delta.x < -INDENT_PX && dragged.parent_id !== null) {
      newParentId = null;
    }

    const movingDown =
      sections.findIndex((s) => s.id === draggedId) <
      sections.findIndex((s) => s.id === targetId);

    // Compute new order explicitly so we can apply it to both local state and cache
    const draggedChildIds = new Set(
      dragged.parent_id === null
        ? sections.filter((s) => s.parent_id === draggedId).map((s) => s.id)
        : []
    );
    const withoutDragged = sections.filter((s) => s.id !== draggedId && !draggedChildIds.has(s.id));
    const draggedChildren = sections.filter((s) => draggedChildIds.has(s.id));
    const tIdx = withoutDragged.findIndex((s) => s.id === targetId);

    let insertIdx: number;
    if (newParentId !== dragged.parent_id && newParentId === targetId) {
      // Becoming child of target: insert after all of target's current children
      insertIdx = tIdx + 1;
      while (insertIdx < withoutDragged.length && withoutDragged[insertIdx].parent_id === targetId)
        insertIdx++;
    } else if (newParentId !== dragged.parent_id && newParentId === null && target.parent_id !== null) {
      // Promoting to root, dropped on a child: place after that child's parent block
      const pIdx = withoutDragged.findIndex((s) => s.id === target.parent_id);
      insertIdx = pIdx + 1;
      while (insertIdx < withoutDragged.length && withoutDragged[insertIdx].parent_id === target.parent_id)
        insertIdx++;
    } else {
      // Same level — insert before or after target's block
      if (movingDown) {
        insertIdx = tIdx + 1;
        if (!target.parent_id) {
          while (insertIdx < withoutDragged.length && withoutDragged[insertIdx].parent_id === targetId)
            insertIdx++;
        }
      } else {
        insertIdx = tIdx;
      }

      // Context-based parent detection: if a child was dragged into a different
      // root's group (without enough horizontal delta to trigger reparent),
      // detect the contextual parent by scanning backwards for the nearest root.
      if (dragged.parent_id !== null) {
        let contextParentId: string | null = null;
        for (let i = insertIdx - 1; i >= 0; i--) {
          if (withoutDragged[i].parent_id === null) {
            contextParentId = withoutDragged[i].id;
            break;
          }
        }
        if (contextParentId !== null && contextParentId !== dragged.parent_id) {
          newParentId = contextParentId;
        }
      }
    }

    const parentChanged = newParentId !== dragged.parent_id;
    const updatedDragged = { ...dragged, parent_id: newParentId };

    insertIdx = Math.max(0, Math.min(insertIdx, withoutDragged.length));
    const newSections = [
      ...withoutDragged.slice(0, insertIdx),
      updatedDragged,
      ...draggedChildren,
      ...withoutDragged.slice(insertIdx),
    ];

    setSections(newSections);

    // Keep cache in sync so navigation away + back shows the correct order
    queryClient.setQueryData<ProjectTemplateData>(templateQueryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        prompt_libraries: newSections.map((s) => ({
          ...old.prompt_libraries.find((pl) => pl.id === s.id)!,
          parent_id: s.parent_id,
        })),
      };
    });

    // Persist new ordinals for every drag (fire-and-forget)
    projectsApi
      .reorderProjectPromptLibraries(projectId, newSections.map((s) => s.id))
      .catch(() => {});

    if (parentChanged) {
      projectsApi
        .updateProjectPromptLibrary(projectId, draggedId, { parent_id: newParentId })
        .catch(() => {});
    }
  };

  // Move a section up or down within its sibling group only.
  // Root sections carry their children with them.
  const moveSection = (id: string, direction: -1 | 1) => {
    const section = sections.find((s) => s.id === id);
    if (!section) return;

    const parentId = section.parent_id;
    const siblings = sections.filter((s) => s.parent_id === parentId);
    const sibIdx = siblings.findIndex((s) => s.id === id);
    const targetSibIdx = sibIdx + direction;
    if (targetSibIdx < 0 || targetSibIdx >= siblings.length) return;
    const targetSibling = siblings[targetSibIdx];

    let newSections: Section[];

    if (parentId !== null) {
      // Children: step one position in the flat array, but only if the neighbor
      // shares the same parent — prevents crossing root-section boundaries.
      const myFlatIdx = sections.findIndex((s) => s.id === id);
      const nextFlatIdx = myFlatIdx + direction;
      if (nextFlatIdx < 0 || nextFlatIdx >= sections.length) return;
      if (sections[nextFlatIdx].parent_id !== parentId) return;
      newSections = arrayMove(sections, myFlatIdx, nextFlatIdx);
    } else {
      // Root: swap entire blocks (self + children)
      const getBlock = (rootId: string): string[] => {
        const block: string[] = [];
        let inside = false;
        for (const s of sections) {
          if (s.id === rootId) { inside = true; block.push(s.id); }
          else if (inside) {
            if (s.parent_id === rootId) block.push(s.id);
            else break;
          }
        }
        return block;
      };

      const myBlockIds = new Set(getBlock(id));
      const targetBlockIds = new Set(getBlock(targetSibling.id));
      const myBlock = sections.filter((s) => myBlockIds.has(s.id));
      const targetBlock = sections.filter((s) => targetBlockIds.has(s.id));
      const others = sections.filter((s) => !myBlockIds.has(s.id) && !targetBlockIds.has(s.id));

      const earlierId = direction === -1 ? targetSibling.id : id;
      const earlierFlatIdx = sections.findIndex((s) => s.id === earlierId);
      let insertInOthers = 0;
      for (let i = 0; i < earlierFlatIdx; i++) {
        if (!myBlockIds.has(sections[i].id) && !targetBlockIds.has(sections[i].id)) insertInOthers++;
      }

      const before = others.slice(0, insertInOthers);
      const after = others.slice(insertInOthers);
      newSections = direction === -1
        ? [...before, ...myBlock, ...targetBlock, ...after]
        : [...before, ...targetBlock, ...myBlock, ...after];
    }

    setSections(newSections);

    // Keep cache in sync so navigation away + back preserves order
    queryClient.setQueryData<ProjectTemplateData>(templateQueryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        prompt_libraries: newSections.map((s) => ({
          ...old.prompt_libraries.find((pl) => pl.id === s.id)!,
          parent_id: s.parent_id,
        })),
      };
    });

    // Persist new ordinals to the backend
    projectsApi
      .reorderProjectPromptLibraries(projectId, newSections.map((s) => s.id))
      .catch(() => {});
  };

  const addChildSection = async (parentId: string) => {
    try {
      const created = await projectsApi.createProjectPromptLibrary(projectId, {
        name: "New section",
        parent_id: parentId,
        template_id: activeTemplate.template.id,
      });
      const newSection: Section = {
        id: created.id,
        name: created.name,
        description: created.description,
        parent_id: created.parent_id,
        priming: null,
        ordinal: created.ordinal,
      };
      const newCachePl = {
        id: created.id,
        source_prompt_library_id: "",
        name: created.name,
        priming: null,
        ordinal: created.ordinal,
        description: created.description,
        parent_id: created.parent_id,
        sources: null,
      } as ProjectTemplateData["prompt_libraries"][number];

      // Ensure parent is expanded so the new child is visible
      setCollapsedSections((prev) => { const next = new Set(prev); next.delete(parentId); return next; });

      // Insert after the last existing child of this parent
      setSections((prev) => {
        const parentIdx = prev.findIndex((s) => s.id === parentId);
        let insertIdx = parentIdx + 1;
        while (insertIdx < prev.length && prev[insertIdx].parent_id === parentId) insertIdx++;
        const next = [...prev];
        next.splice(insertIdx, 0, newSection);
        return next;
      });

      queryClient.setQueryData<ProjectTemplateData>(templateQueryKey, (old) => {
        if (!old) return old;
        const pls = [...old.prompt_libraries];
        const parentIdx = pls.findIndex((pl) => pl.id === parentId);
        let insertIdx = parentIdx + 1;
        while (insertIdx < pls.length && pls[insertIdx].parent_id === parentId) insertIdx++;
        pls.splice(insertIdx, 0, newCachePl);
        return { ...old, prompt_libraries: pls };
      });
    } catch {
      // silently ignore
    }
  };

  const addRootSection = async () => {
    try {
      const created = await projectsApi.createProjectPromptLibrary(projectId, {
        name: "New section",
        template_id: activeTemplate.template.id,
      });
      const newSection: Section = {
        id: created.id,
        name: created.name,
        description: created.description,
        parent_id: created.parent_id,
        priming: null,
        ordinal: created.ordinal,
      };
      const newCachePl = {
        id: created.id,
        source_prompt_library_id: "",
        name: created.name,
        priming: null,
        ordinal: created.ordinal,
        description: created.description,
        parent_id: created.parent_id,
        sources: null,
      } as ProjectTemplateData["prompt_libraries"][number];

      setSections((prev) => [...prev, newSection]);

      queryClient.setQueryData<ProjectTemplateData>(templateQueryKey, (old) => {
        if (!old) return old;
        return { ...old, prompt_libraries: [...old.prompt_libraries, newCachePl] };
      });
    } catch {
      // silently ignore
    }
  };

  // Compute where the drop-indicator line should appear during a drag
  const computeDropLine = (
    secs: Section[],
    activeId: string | null,
    overId: string | null,
    deltaX: number,
  ): { afterIndex: number; indent: boolean } | null => {
    if (!activeId || !overId || activeId === overId) return null;
    const dragged = secs.find((s) => s.id === activeId);
    const target = secs.find((s) => s.id === overId);
    if (!dragged || !target) return null;

    const INDENT_PX = 50;
    let newParentId = dragged.parent_id;
    if (deltaX > INDENT_PX && !target.parent_id && target.id !== activeId)
      newParentId = target.id;
    else if (deltaX < -INDENT_PX && dragged.parent_id !== null)
      newParentId = null;

    const indent = newParentId !== null;
    const movingDown =
      secs.findIndex((s) => s.id === activeId) < secs.findIndex((s) => s.id === overId);

    const tIdx = secs.findIndex((s) => s.id === overId);
    let afterIndex: number;
    if (movingDown) {
      afterIndex = tIdx;
      if (!target.parent_id && newParentId !== target.id) {
        // root-level reorder: skip over the target's children
        let i = tIdx + 1;
        while (i < secs.length && secs[i].parent_id === target.id) i++;
        afterIndex = i - 1;
      }
    } else {
      afterIndex = tIdx - 1;
    }
    return { afterIndex: Math.max(0, afterIndex), indent };
  };

  return (
    <div className="border">
      {/* Column headers */}
      <div className="flex items-center gap-3 bg-[#F7F6EE] border-b-2 border-[#061D4A] p-3">
        <span className="w-4" />
        <span className="w-6" />
        <span className="flex-1 text-sm font-semibold text-foreground">Section Title</span>
        <span className="w-44 shrink-0 text-sm font-semibold text-foreground">Sources</span>
        <span className="w-40 text-sm text-right font-semibold text-foreground pr-1">Actions</span>
      </div>

      {sections.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No sections found for this template.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(e: DragStartEvent) => {
            setActiveDragId(e.active.id as string);
            setDragOverId(null);
            setDragDeltaX(0);
          }}
          onDragMove={(e: DragMoveEvent) => {
            setDragOverId((e.over?.id as string) ?? null);
            setDragDeltaX(e.delta.x);
          }}
          onDragEnd={(e) => {
            setActiveDragId(null);
            setDragOverId(null);
            setDragDeltaX(0);
            handleDragEnd(e);
          }}
        >
          <SortableContext items={sections.filter((s) => s.parent_id === null || !collapsedSections.has(s.parent_id)).map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {(() => {
              const { rootNumbers, childCounters, parentIds } = buildLabelData(sections);
              const filterSection = filterSectionId ? sections.find((s) => s.id === filterSectionId) : null;
              const visibleSections = sections.filter((s) => {
                if (filterSection) {
                  if (filterSection.parent_id === null) {
                    if (s.id !== filterSection.id && s.parent_id !== filterSection.id) return false;
                  } else {
                    if (s.id !== filterSection.id) return false;
                  }
                }
                return s.parent_id === null || !collapsedSections.has(s.parent_id);
              });
              return visibleSections.map((section, index) => {
                const dropLine = computeDropLine(visibleSections, activeDragId, dragOverId, dragDeltaX);
                const siblings = sections.filter((s) => s.parent_id === section.parent_id);
                const sibIdx = siblings.findIndex((s) => s.id === section.id);
                let label: string;
                if (section.parent_id === null) {
                  label = `${rootNumbers.get(section.id)}`;
                } else {
                  const parentNum = rootNumbers.get(section.parent_id) ?? 0;
                  const childNum = (childCounters.get(section.parent_id) ?? 0) + 1;
                  childCounters.set(section.parent_id, childNum);
                  label = `${parentNum}.${childNum}`;
                }
                return (
                  <div key={section.id}>
                    <SortableRow
                      section={section}
                      label={label}
                      isFirst={sibIdx === 0}
                      isLast={sibIdx === siblings.length - 1}
                      isChild={!!section.parent_id}
                      projectId={projectId}
                      templateQueryKey={templateQueryKey}
                      sectionSources={selectedSources[section.id] ?? { documentIds: [], extractionPointIds: [], artifactIds: [] }}
                      documents={documents}
                      extractionPoints={extractionPoints}
                      artifacts={artifacts}
                      gridReview={gridReview}
                      onMoveUp={() => moveSection(section.id, -1)}
                      onMoveDown={() => moveSection(section.id, 1)}
                      onDelete={() => setPendingDeleteId(section.id)}
                      onAddChild={() => addChildSection(section.id)}
                      onOpenSources={() => setSourceModalSectionId(section.id)}
                      isCollapsible={!section.parent_id && parentIds.has(section.id)}
                      isCollapsed={collapsedSections.has(section.id)}
                      onToggleCollapse={() => toggleCollapse(section.id)}
                    />
                    {dropLine && dropLine.afterIndex === index && (
                      <div
                        className="h-0.5 bg-primary rounded my-0.5"
                        style={{ marginLeft: dropLine.indent ? 28 : 0 }}
                      />
                    )}
                  </div>
                );
              });
            })()}
          </SortableContext>
        </DndContext>
      )}

      {/* Add root section */}
      <button
        onClick={addRootSection}
        className="w-full flex items-center gap-2 p-4 text-sm text-muted-foreground hover:text-foreground transition-colors border-t"
      >
        <Plus className="w-4 h-4" />
        Add new section…
      </button>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!pendingDeleteId} onOpenChange={(open) => { if (!open) setPendingDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete section</AlertDialogTitle>
            <AlertDialogDescription>
              {sections.find((s) => s.id === pendingDeleteId)?.parent_id === null &&
              sections.some((s) => s.parent_id === pendingDeleteId)
                ? "This will permanently delete the section and all its subsections. This action cannot be undone."
                : "This will permanently delete the section. This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteSection}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Source selection modal */}
      {sourceModalSectionId && (() => {
        const current = selectedSources[sourceModalSectionId] ?? { documentIds: [], extractionPointIds: [], artifactIds: [] };
        const section = sections.find((s) => s.id === sourceModalSectionId);
        return (
          <SectionSourceModal
            open={true}
            onClose={() => setSourceModalSectionId(null)}
            sectionName={section?.name ?? ""}
            documents={documents}
            extractionPoints={extractionPoints}
            extractionDocuments={extractionDocuments}
            gridReview={gridReview}
            isExtractionLoading={isExtractionLoading}
            hasExtractionError={hasExtractionError}
            selectedDocumentIds={current.documentIds}
            selectedExtractionPointIds={current.extractionPointIds}
            artifacts={artifacts}
            selectedArtifactIds={current.artifactIds ?? []}
            onSave={handleSourceSave}
          />
        );
      })()}
    </div>
  );
};

// Re-export types that consumers depend on
export type { Section, SectionSources };
